/**
 * 角色管理模块 - 完整的CRUD功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Input, Select, Space, Modal, message, Form, Switch, Tag, Tree, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { roleService } from '../../../../../services/roleService';
import { menuService } from '../../../../../services/menuService';
import { permissionService } from '../../../../../services/permissionService';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import QueryArea, { QueryField } from '../../components/QueryArea';

const { TextArea } = Input;

interface Role {
  id: number;
  roleName: string;
  roleKey: string;
  description: string;
  permissions: string[];
  menuIds: number[]; // 添加菜单ID数组
  status: number;
  createTime: string;
  updateTime?: string;
}

interface Menu {
  id: number;
  menuName: string;
  parentId: number;
  perms: string;
  children?: Menu[];
  key?: string; // 修改为string类型用于Tree组件
}

const RoleManagement: React.FC = () => {
  const [dataSource, setDataSource] = useState<Role[]>([]);
  const [menuTree, setMenuTree] = useState<Menu[]>([]); // 添加菜单树状态
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    status: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Role | null>(null);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '角色名',
      type: 'input',
      placeholder: '搜索角色名或角色键...',
      width: 200,
    },
    {
      key: 'status',
      label: '状态',
      type: 'select',
      placeholder: '选择状态',
      width: 120,
      options: [
        { label: '正常', value: '1' },
        { label: '禁用', value: '0' },
      ],
    },
  ];

  // 表格列定义 - 优化后只显示：角色名称、权限、状态，无滚动条
  const columns: ColumnsType<Role> = [
    {
      title: '角色名称',
      dataIndex: 'roleName',
      key: 'roleName',
      width: 120,
      ellipsis: true,
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 200, // 进一步减少权限列宽度
      render: (permissions: string[]) => (
        <Space wrap>
          {permissions?.slice(0, 1).map(permission => (
            <Tag key={permission} color="blue">{permission}</Tag>
          ))}
          {permissions?.length > 1 && <Tag color="default">+{permissions.length - 1}</Tag>}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      align: 'center',
      render: (value) => (
        <Tag color={value === 1 ? 'green' : 'red'}>
          {value === 1 ? '正常' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Button 
            type="link" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={record.roleKey === 'admin'}
            style={{ padding: '4px 8px' }}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            size="small" 
            icon={<SettingOutlined />}
            onClick={() => handlePermission(record)}
            style={{ padding: '4px 8px' }}
          >
            权限
          </Button>
          <Button 
            type="link" 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            disabled={record.roleKey === 'admin'}
            style={{ padding: '4px 8px' }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 数据加载
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        current,
        size: pageSize,
        ...searchParams,
      };

      const response = await roleService.getList(params);
      if (response.code === 200) {
        setDataSource(response.data.records || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchParams]);

  // 加载菜单树
  const loadMenuTree = async () => {
    try {
      const response = await menuService.getList({ status: 1, size: 1000 }); // 获取所有正常状态的菜单
      if (response.code === 200) {
        const menus = response.data.records || [];
        // 构建树形结构
        const treeData = buildMenuTree(menus);
        setMenuTree(treeData);
      }
    } catch (error) {
      console.error('加载菜单树失败:', error);
    }
  };

  // 构建菜单树
  const buildMenuTree = (menus: Menu[]): Menu[] => {
    const map = new Map<number, Menu & { children: Menu[] }>();
    const roots: Menu[] = [];

    // 初始化所有节点
    menus.forEach(menu => {
      map.set(menu.id, { ...menu, children: [] });
    });

    // 构建树形结构
    menus.forEach(menu => {
      const node = map.get(menu.id)!;
      if (menu.parentId === 0) {
        roots.push(node);
      } else {
        const parent = map.get(menu.parentId);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    return roots;
  };

  useEffect(() => {
    loadData();
  }, [current, pageSize, searchParams]);

  useEffect(() => {
    loadMenuTree(); // 加载菜单树
  }, []);

  // 搜索
  const handleSearch = () => {
    setCurrent(1);
    loadData();
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ keyword: '', status: '' });
    setCurrent(1);
    setTimeout(loadData, 0);
  };

  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      keyword: values.keyword || '',
      status: values.status || '',
    });
  }, []);

  // 新增
  const handleAdd = () => {
    setEditingRecord(null);
    setCheckedKeys([]);
    form.resetFields();
    setIsModalVisible(true);
  };

  // 编辑
  const handleEdit = async (record: Role) => {
    // 检查是否是超级管理员角色，如果是则不允许编辑
    if (record.roleKey === 'admin') {
      message.error('不能编辑超级管理员角色');
      return;
    }
    
    setEditingRecord(record);
    setIsModalVisible(true);
    
    // 加载角色的菜单权限
    try {
      const response = await permissionService.getRoleMenus(record.id);
      
      if (response.code === 200) {
        const menuIds = response.data || [];
        
        // 后端返回的是菜单ID列表（Long类型），直接使用
        // 确保ID是数字类型（处理可能的类型转换问题）
        const menuIdList = menuIds.map((id: any) => {
          // 处理可能的类型转换：字符串或数字都转换为数字
          if (typeof id === 'string') {
            return parseInt(id, 10);
          }
          return Number(id);
        }).filter((id: number) => !isNaN(id) && id > 0);
        
        setCheckedKeys(menuIdList);
        form.setFieldsValue({
          ...record,
          menuIds: menuIdList,
        });
        
      } else {
        setCheckedKeys([]);
        form.setFieldsValue({
          ...record,
          menuIds: [],
        });
      }
    } catch (error) {
      console.error('加载角色菜单权限失败:', error);
      setCheckedKeys([]);
      form.setFieldsValue({
        ...record,
        menuIds: [],
      });
    }
  };

  // 权限设置
  const handlePermission = (record: Role) => {
    Modal.info({
      title: `${record.roleName} - 权限设置`,
      content: (
        <div>
          <p><strong>角色标识：</strong>{record.roleKey}</p>
          <p><strong>描述：</strong>{record.description}</p>
          <p><strong>当前权限：</strong></p>
          <Space wrap>
            {record.permissions?.map(permission => (
              <Tag key={permission} color="blue">{permission}</Tag>
            ))}
          </Space>
        </div>
      ),
      width: 600,
    });
  };

  // 删除
  const handleDelete = (id: number) => {
    const role = dataSource.find(r => r.id === id);
    if (role?.roleKey === 'admin') {
      message.error('不能删除超级管理员角色');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个角色吗？删除后无法恢复。',
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await roleService.delete(id);
          if (response.code === 200) {
            message.success('删除成功');
            loadData();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }

    const hasAdmin = selectedRowKeys.some(id => {
      const role = dataSource.find(r => r.id === id);
      return role?.roleKey === 'admin';
    });

    if (hasAdmin) {
      message.error('选中的角色中包含超级管理员，不能删除');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个角色吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await roleService.batchDelete(selectedRowKeys as number[]);
          if (response.code === 200) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            loadData();
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 表单提交
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      let response;
      let permissionSuccess = true;
      let permissionMessage = '';
      
      if (editingRecord) {
        // 检查是否是超级管理员角色
        if (editingRecord.roleKey === 'admin') {
          message.error('不能编辑超级管理员角色');
          setLoading(false);
          return;
        }
        
        // 更新角色信息
        response = await roleService.update({ ...values, id: editingRecord.id });
        
        // 更新角色菜单权限
        if (response.code === 200 && values.menuIds && values.menuIds.length > 0) {
          try {
            // 将Integer类型的菜单ID转换为Long类型
            const longMenuIds = values.menuIds.map((id: any) => Number(id));
            const permissionResponse = await permissionService.assignRoleMenus(editingRecord.id, longMenuIds);
            if (permissionResponse.code !== 200) {
              permissionSuccess = false;
              permissionMessage = permissionResponse.message || '未知错误';
            }
          } catch (error) {
            permissionSuccess = false;
            permissionMessage = '菜单权限分配失败';
            console.error('分配菜单权限失败:', error);
          }
        }
      } else {
        // 创建角色
        response = await roleService.create(values);
        
        // 分配角色菜单权限
        if (response.code === 200 && response.data && response.data.id) {
          const newRoleId = response.data.id;
          if (values.menuIds && values.menuIds.length > 0) {
            try {
              // 将Integer类型的菜单ID转换为Long类型
              const longMenuIds = values.menuIds.map((id: any) => Number(id));
              const permissionResponse = await permissionService.assignRoleMenus(newRoleId, longMenuIds);
              if (permissionResponse.code !== 200) {
                permissionSuccess = false;
                permissionMessage = permissionResponse.message || '未知错误';
              }
            } catch (error) {
              permissionSuccess = false;
              permissionMessage = '菜单权限分配失败';
              console.error('分配菜单权限失败:', error);
            }
          }
        }
      }

      if (response.code === 200) {
        if (permissionSuccess) {
          message.success(editingRecord ? '编辑成功' : '新增成功');
        } else {
          message.warning((editingRecord ? '编辑成功' : '新增成功') + '，但菜单权限分配失败：' + permissionMessage);
        }
        setIsModalVisible(false);
        loadData();
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="role-management">
      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        onBatchDelete={handleBatchDelete}
        batchDeleteDisabled={selectedRowKeys.length === 0}
        selectedCount={selectedRowKeys.length}
        addButtonText="新增角色"
        batchDeleteButtonText="批量删除"
      />

      {/* 数据表格 */}
      <div className="table-card">
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: record.roleKey === 'admin',
            }),
          }}
          pagination={{
            current,
            pageSize,
            total,
            ...PAGINATION_CONFIG.default,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size);
            },
          }}
        />
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑角色' : '新增角色'}
        open={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          initialValues={{
            status: 1,
            menuIds: [],
          }}
        >
          {/* 第一排：角色名称和角色标识 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="roleName"
                label="角色名称:"
                rules={[{ required: true, message: '请输入角色名称' }]}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Input placeholder="请输入角色名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="roleKey"
                label="角色标识:"
                rules={[
                  { required: true, message: '请输入角色标识' },
                  { pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/, message: '角色标识只能包含字母、数字和下划线，且以字母开头' }
                ]}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Input placeholder="请输入角色标识" disabled={!!editingRecord} />
              </Form.Item>
            </Col>
          </Row>

          {/* 第二排：角色描述 */}
          <Form.Item
            name="description"
            label="角色描述:"
            labelAlign="left"
          >
            <TextArea rows={3} placeholder="请输入角色描述（可选）" />
          </Form.Item>

          {/* 第三排：菜单权限 */}
          <Form.Item
            name="menuIds"
            label="菜单权限:"
            rules={[{ required: true, message: '请选择菜单权限' }]}
            labelAlign="left"
          >
            <Tree
              checkable
              checkedKeys={checkedKeys}
              onCheck={(checkedKeys, info) => {
                
                setCheckedKeys(checkedKeys as React.Key[]);
                form.setFieldValue('menuIds', checkedKeys);
                
                // 验证表单值是否正确设置
                setTimeout(() => {
                  const formValues = form.getFieldsValue();
                }, 100);
              }}
              treeData={menuTree.map(menu => ({
                ...menu,
                key: menu.id,
                title: menu.menuName,
                children: menu.children?.map(child => ({
                  ...child,
                  key: child.id,
                  title: child.menuName,
                  children: child.children?.map(grandChild => ({
                    ...grandChild,
                    key: grandChild.id,
                    title: grandChild.menuName
                  }))
                }))
              })) as any}
              fieldNames={{ title: 'title', key: 'key', children: 'children' }}
              checkStrictly={false}
              defaultExpandAll
              style={{ maxHeight: 300, overflow: 'auto' }}
            />
          </Form.Item>

          {/* 第四排：状态 */}
          <Form.Item
            name="status"
            label="状态:"
            valuePropName="checked"
            labelAlign="left"
          >
            <Switch checkedChildren="正常" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagement;
